import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { InfoCircleOutlined, CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';
import { enqueueSnackbar } from 'notistack';

const AssignAdminsModal = ({ visible, onClose, meetingId, onAssignSuccess }) => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAdminIds, setSelectedAdminIds] = useState([]);
    const pageSize = 10;

    // --- Загрузка всех админов + текущих назначений ---
    useEffect(() => {
        if (!visible || !meetingId) return;

        const loadAdmins = async () => {
            setLoading(true);
            const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
            try {
                const response = await fetch(
                    `${API_ADDRESS}get_admins_with_assignment/?meeting_id=${meetingId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );

                if (response.ok) {
                    const data = await response.json();

                    setAdmins(data);

                    // Извлекаем тех, кто уже назначен
                    const assignedIds = data
                        .filter(admin => admin.is_assigned)
                        .map(admin => admin.id);

                    setSelectedAdminIds(assignedIds);
                } else {
                    const error = await response.json();
                    enqueueSnackbar(`Ошибка загрузки: ${error.error}`, { variant: 'error' });
                }
            } catch (err) {
                console.error('Ошибка при загрузке администраторов:', err);
                enqueueSnackbar('Не удалось подключиться к серверу', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        loadAdmins();
    }, [visible, meetingId]);

    // --- Поиск и фильтры ---
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleEnterSearch = (e) => {
        if (e.key === 'Enter' && searchQuery.trim() !== '') {
            setFilters(prev => [...prev, searchQuery.trim()]);
            setSearchQuery('');
            setCurrentPage(1);
        }
    };

    const removeFilter = (filter) => {
        setFilters(prev => prev.filter(f => f !== filter));
        setCurrentPage(1);
    };

    const renderFilters = () => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
            {filters.map((f, i) => (
                <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px'
                }}>
                    {f}
                    <span
                        onClick={() => removeFilter(f)}
                        style={{ marginLeft: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        ×
                    </span>
                </div>
            ))}
        </div>
    );

    // --- Фильтрация и пагинация ---
    const getFilteredAndPaginatedAdmins = () => {
        const activeFilters = [...filters];
        if (searchQuery.trim()) activeFilters.push(searchQuery.trim());

        let filtered = admins.filter(admin => {
            const fullName = admin.full_name?.toLowerCase() || '';
            const username = admin.username?.toLowerCase() || '';
            const email = admin.email?.toLowerCase() || '';
            return activeFilters.every(filter => {
                const lowerFilter = filter.toLowerCase();
                return (
                    fullName.includes(lowerFilter) ||
                    username.includes(lowerFilter) ||
                    email.includes(lowerFilter)
                );
            });
        });

        const startIndex = (currentPage - 1) * pageSize;
        return filtered.slice(startIndex, startIndex + pageSize);
    };

    const totalPages = Math.ceil(
        admins.filter(admin => {
            const fullName = admin.full_name?.toLowerCase() || '';
            const username = admin.username?.toLowerCase() || '';
            const email = admin.email?.toLowerCase() || '';
            const activeFilters = [...filters];
            if (searchQuery.trim()) activeFilters.push(searchQuery.trim());
            if (activeFilters.length === 0) return true;
            return activeFilters.every(filter => {
                const lowerFilter = filter.toLowerCase();
                return (
                    fullName.includes(lowerFilter) ||
                    username.includes(lowerFilter) ||
                    email.includes(lowerFilter)
                );
            });
        }).length / pageSize
    );

    // --- Переключение выбора при клике на строку ---
    const handleRowClick = (adminId) => {
        setSelectedAdminIds(prev =>
            prev.includes(adminId)
                ? prev.filter(id => id !== adminId)
                : [...prev, adminId]
        );
    };

    // --- Сохранение выбранных администраторов ---
    const handleSave = async () => {
        if (!meetingId) {
            message.error('Не указан ID собрания');
            return;
        }

        try {
            const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
            const response = await fetch(`${API_ADDRESS}assign_admin_to_meetings_v2/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    user_id: null,
                    meeting_ids: [meetingId],
                    admin_ids: selectedAdminIds,
                }),
            });

            if (response.ok) {
                enqueueSnackbar('Администраторы обновлены', { variant: 'success' });
                onAssignSuccess?.();
                onClose();
            } else {
                const error = await response.json();
                enqueueSnackbar(`Ошибка: ${error.error}`, { variant: 'error' });
            }
        } catch (err) {
            console.error('Ошибка при сохранении:', err);
            enqueueSnackbar('Сетевая ошибка', { variant: 'error' });
        }
    };

    return (
        <Modal
            title="Назначить администраторов на собрание"
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Отмена
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Сохранить
                </Button>,
            ]}
            width={538}
            style={{ top: 20 }}
            bodyStyle={{
                padding: '16px',
                height: 'calc(417px - 110px)',
                display: 'flex',
                flexDirection: 'column',
                margin: 0,
            }}
        >
            {/* Блок поиска */}
            <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Input
                        placeholder="Поиск по имени"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleEnterSearch}
                        style={{ width: 300 }}
                    />
                </div>
                {renderFilters()}
            </div>

            {/* Таблица с центрированием и симметричными отступами */}
            <div
                style={{
                    width: '100%',
                    maxWidth: 490,
                    margin: '0 auto', // Центрируем
                    maxHeight: 213,
                    overflowY: 'auto',
                    border: '1px solid #e8e8e8',
                    borderRadius: '4px',
                    position: 'relative',
                    flex : 1
                }}
            >
                {/* Заголовок таблицы */}
                <div
                    style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#f5f5f5',
                        zIndex: 1,
                        borderBottom: '1px solid #e8e8e8',
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#000',
                    }}
                >
                    <div>Имя пользователя</div>
                </div>

                {/* Строки таблицы */}
                {getFilteredAndPaginatedAdmins().map((admin, index) => {
                    const isSelected = selectedAdminIds.includes(admin.id);

                    return (
                        <div
                            key={admin.id}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr',
                                padding: '10px 16px',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '14px',
                                alignItems: 'center',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#e6f7ff' : 'white',
                                borderLeft: isSelected ? '4px solid #1890ff' : '4px solid transparent',
                                transition: 'all 0.2s ease',
                            }}
                            onClick={() => handleRowClick(admin.id)}
                        >
                            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {admin.full_name || <em>Без имени</em>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
                (() => {
                    const groupSize = 10;
                    const currentGroup = Math.ceil(currentPage / groupSize);
                    const startPage = (currentGroup - 1) * groupSize + 1;
                    const endPage = Math.min(currentGroup * groupSize, totalPages);

                    const goToPage = (page) => {
                        if (page >= 1 && page <= totalPages) {
                            this.setState({
                                currentPage: page,
                                selectedIndex: null,
                                selectedUser: null,
                            });
                        }
                    };

                    const goToPrevGroup = () => {
                        const prevStart = startPage - groupSize;
                        const newPage = prevStart >= 1 ? prevStart : 1;
                        this.setState({
                            currentPage: newPage,
                            selectedIndex: null,
                            selectedUser: null,
                        });
                    };

                    const goToNextGroup = () => {
                        const nextStart = startPage + groupSize;
                        if (nextStart <= totalPages) {
                            this.setState({
                                currentPage: nextStart,
                                selectedIndex: null,
                                selectedUser: null,
                            });
                        }
                    };

                    return (
                        <div style={{ width: '100%' }}>
                            {/* Первая строка — номера страниц */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                    gap: '4px',
                                    marginBottom: '8px',
                                }}
                            >
                                {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                    const page = startPage + i;
                                    return (
                                        <Button
                                            key={page}
                                            type={currentPage === page ? 'primary' : 'default'}
                                            size="small"
                                            onClick={() => goToPage(page)}
                                            style={{
                                                minWidth: '32px',
                                                padding: '0 6px',
                                                fontSize: '12px',
                                                marginTop: '8px',
                                            }}
                                        >
                                            {page}
                                        </Button>
                                    );
                                })}
                            </div>

                            {/* Вторая строка — стрелки перелистывания */}
                            {(currentGroup > 1 || currentGroup * groupSize < totalPages) && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: '4px',
                                    }}
                                >
                                    {currentGroup > 1 && (
                                        <Button
                                            type="default"
                                            size="small"
                                            onClick={goToPrevGroup}
                                            icon={<CaretLeftOutlined style={{ fontSize: '12px' }} />}
                                            style={{ fontWeight: 'bold' }}
                                        />
                                    )}
                                    {currentGroup * groupSize < totalPages && (
                                        <Button
                                            type="default"
                                            size="small"
                                            onClick={goToNextGroup}
                                            icon={<CaretRightOutlined style={{ fontSize: '12px' }} />}
                                            style={{ fontWeight: 'bold' }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()
            )}

            {/* Статистика */
            <div style={{ marginTop: '10px', fontSize: '13px', color: '#666', textAlign: 'right' }}>
                Выбрано: <b>{selectedAdminIds.length}</b> администраторов
            </div>}
        </Modal>
    );
};

export default AssignAdminsModal;